import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { SessionStepTitle } from '@/components';
import { useGetSessions } from '@/services';
import { warmPaymentCheckoutCacheFromSession } from '@/utils';
import { scrollSchedulingWizardToTop } from '@/utils/scrollSchedulingWizardToTop';
import { getUserData } from '@/utils/cookies';
import { useModal } from '@/hooks';
import { PaymentContainer } from '../components';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
import { UserHomeServiceSearch } from './UserHomeServiceSearch';
import { UserHomePlansSection } from './UserHomePlansSection';
import { UserHomeClubSection } from './UserHomeClubSection';
import { UserHomeScheduleBanner } from './UserHomeScheduleBanner';
import { UserHomeContinueWithProfessionalCard } from './UserHomeContinueWithProfessionalCard';
import { useLastFinishedPsychologistSession } from './useLastFinishedPsychologistSession';
import { UserHomeUpcomingSessionsCarousel } from './UserHomeUpcomingSessionsCarousel';
import { UserHomeGreetingNotification } from './UserHomeGreetingNotification';
import { pickRandomUserHomeGreetingMessage } from './userHomeGreetingMessages';
import { cn } from '@/lib/utils';
import { isUserHomeGreetingDismissedForPageLoad } from './utils/userHomeGreetingDismissed';
import { userHomeSectionGapClassName } from './userHomeCardStyles';
import { getUserHomeUpcomingSessionsDateRange } from '../utils/dates';

const UserHome = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const {
		openModal,
		closeAllModals,
		setOnConfirm,
		setConfirmText,
		setCancelText,
	} = useModal();

	useEffect(() => {
		scrollSchedulingWizardToTop('auto');
	}, []);

	const upcomingSessionsDateRange = useMemo(
		() => getUserHomeUpcomingSessionsDateRange(),
		[],
	);

	const { data: sessions, isLoading } = useGetSessions(
		{
			isMe: true,
			limit: 50,
			page: 1,
			statusList: ['CREATED', 'CONFIRMED', 'STARTED'],
			date_a: upcomingSessionsDateRange.date_a,
			date_b: upcomingSessionsDateRange.date_b,
			sort: 'selected_date',
			sort_direction: 'asc',
		},
		{ refetchOnMount: 'always' },
	);


	const openSessionPaymentModal = useCallback(
		(session: Sessions.Session) => {
			const professionalIdForReturn =
				session.professional_id ?? session.professional?.id ?? '';

			openModal({
				size: 'wide',
				mobileSheet: 'tall',
				title: (
					<SessionStepTitle
						icon={<Wallet className="w-4" />}
						title="Confirmar e pagar"
					/>
				),
				content: (
					<div
						className="flex flex-col gap-4"
						data-testid={E2E_SCHEDULING.PAYMENT_ROOT}
					>
						<PaymentContainer
							session={session}
							embeddedInModal
							navigate={navigate}
							schedulingCheckoutReturn={
								professionalIdForReturn
									? {
											professionalId:
												professionalIdForReturn,
											sessionId: session.id,
									  }
									: undefined
							}
							onPaymentClick={closeAllModals}
							onPaymentSettled={closeAllModals}
						/>
					</div>
				),
			});
			setConfirmText('');
			setCancelText('');
			setOnConfirm(() => {});
		},
		[
			closeAllModals,
			navigate,
			openModal,
			setCancelText,
			setConfirmText,
			setOnConfirm,
		],
	);

	const openSessionPaymentModalAndWarm = useCallback(
		(session: Sessions.Session) => {
			warmPaymentCheckoutCacheFromSession(queryClient, session);
			openSessionPaymentModal(session);
		},
		[openSessionPaymentModal, queryClient],
	);

	const handleOnPayment = (session: Sessions.Session) => {
		openSessionPaymentModalAndWarm(session);
	};

	const userData = getUserData();
	const userGreetingName = userData?.first_name?.trim() || 'você';
	const [greetingMessage] = useState(() =>
		pickRandomUserHomeGreetingMessage(),
	);

	const upcomingSessions = sessions?.data ?? [];
	const hasUpcomingSessions = upcomingSessions.length > 0;

	// Quem já fez sessão de psicólogo e não tem consulta futura vê o card
	// "continuar com X" no lugar do banner genérico de agendamento.
	const {
		session: lastPsychologistSession,
		service: psychologistService,
		isLoading: isLoadingLastPsychologistSession,
	} = useLastFinishedPsychologistSession();
	const showContinueWithProfessional =
		!hasUpcomingSessions && !!lastPsychologistSession;

	return (
		<section
			data-site-tutorial="home-root"
			className={cn(
				'relative flex w-full max-w-7xl shrink-0 flex-col px-4 py-6 pb-40 md:pb-52',
				userHomeSectionGapClassName,
			)}
		>
			{isLoading ||
			(!hasUpcomingSessions && isLoadingLastPsychologistSession) ? (
				<UserHomeUpcomingSessionsCarousel
					sessions={[]}
					isLoading
					onPayment={handleOnPayment}
				/>
			) : hasUpcomingSessions ? (
				<UserHomeUpcomingSessionsCarousel
					sessions={upcomingSessions}
					isLoading={false}
					onPayment={handleOnPayment}
				/>
			) : showContinueWithProfessional && lastPsychologistSession ? (
				<UserHomeContinueWithProfessionalCard
					session={lastPsychologistSession}
					service={psychologistService}
				/>
			) : (
				<UserHomeScheduleBanner
					onSchedule={() =>
						navigate(`/consulta?${searchParams.toString()}`)
					}
				/>
			)}

			<UserHomeServiceSearch
				showFeaturedPsychologists={!isLoading && !hasUpcomingSessions}
			/>

			<UserHomeClubSection />

			<UserHomePlansSection />

			{!isUserHomeGreetingDismissedForPageLoad() ? (
				<UserHomeGreetingNotification
					userGreetingName={userGreetingName}
					message={greetingMessage}
				/>
			) : null}
		</section>
	);
};

export { UserHome };
